import { Injectable } from '@angular/core';
import {ApiService} from "../global/api.service";
import {ToastService} from "./toast.service";
import {AuthStorageService} from '../storage/auth-storage.service';
import {HttpBackend, HttpClient} from '@angular/common/http';
import {HasImages} from '../../types/interfaces';

@Injectable({
  providedIn: 'root'
})
export class FileService {

  public static readonly DEFAULT_AVATAR = 'assets/icons/kiwi-user.svg';
  public static readonly DEFAULT_PRODUCT = 'assets/logo_with_border.png';
  // our bucket of the product images: the api stores a 320 pixel webp next to each image (thumbnail.service.js, mediumKey)
  static readonly IMAGE_BUCKET = 'https://s3-eu-central-1.ionoscloud.com/kiwi/';

  // url of the 320 pixel version of a product image for lists, the image itself for other servers (they have no version)
  // the image itself of a version of our bucket: '…png-thumb' or '…png-320' -> '…png'
  static originalImage(url: string): string {
    if (!url) {
      return url;
    }
    const clean = url.split('?')[0];
    return clean.startsWith(FileService.IMAGE_BUCKET) && /-(thumb|320)$/.test(clean) ? clean.replace(/-(thumb|320)$/, '') : url;
  }

  // a missing list image: first the image itself, then the placeholder
  static nextFallbackImage(current: string, listImage: string, placeholder: string): string {
    const original = FileService.originalImage(listImage);
    return !current && original && original !== listImage ? original : placeholder;
  }

  static image320(url: string): string {
    if (!url) {
      return url;
    }
    const clean = url.split('?')[0];
    const fileName = clean.startsWith(FileService.IMAGE_BUCKET) ? clean.slice(FileService.IMAGE_BUCKET.length) : '';
    return fileName && !fileName.includes('/') && !/-(thumb|320)$/.test(fileName) ? `${clean}-320` : url;
  }
  public static readonly DEFAULT_CATEGORY = 'assets/icons/kiwi-logo.svg';

  // icon of a category: a generated icon is an url (shown as image), a chosen one the file name of
  // assets/icons/categories (svg, shown as ion-icon)
  static isIconUrl(icon: string): boolean {
    return /^https?:\/\//i.test(icon || '');
  }

  static categoryIconSrc(icon: string): string {
    if (!icon) {
      return null;
    }
    return FileService.isIconUrl(icon) || icon.startsWith('/') || icon.startsWith('assets/') ? icon : '/assets/icons/categories/' + icon;
  }
  public static readonly DEFAULT_COMPETITION = 'assets/competition_placeholder.jpg';

  private imageNameCache = {};
  private isUploading = false;
  private http: HttpClient ;

  constructor(
    private api: ApiService,
    private storageService: AuthStorageService,
    private toastService: ToastService,
    handler: HttpBackend,
  ) {
    this.http = new HttpClient(handler);
  }

  public async loadTextFile(path: string): Promise<any> {
    try {
      return await this.http.get(path, { responseType: 'text'}).toPromise()
    } catch (e) {
      console.log(e);
      return null;
    }
  }

  public async saveFiles (obj: HasImages, newImages: File[], type: string) {
    if (!newImages || newImages.length === 0) {
      return;
    }
    const images = obj.Images && obj.Images.length > 0 ? [...obj.Images] : [];
    for (const file of newImages) {
      const url = await this.post(file, type);
      if (url) {
        images.push(url);
      }
    }
    obj.Images = images;
  }

  public async saveFile<T extends {Image?: string}> (obj: T, newImage: File, type: string) {
    if (obj && newImage) {
      const url = await this.post(newImage, type);
      if (url) {
        obj.Image = url;
      }
    }
  }

  public async post(file: File, type: string): Promise<string> {
    try {
      const formData: FormData = new FormData();
      formData.append('file', file, file.name);
      formData.append('Type', type);
      return this.upload(formData);
    } catch (error) {
      this.toastService.presentError(error);
      return null;
    }
  }

  private async upload(formData: FormData): Promise<string> {
    const apiKey = await this.storageService.getApiKey();
    const http = new XMLHttpRequest();
    return new Promise<string>((resolve, reject) => {
      http.open('POST', this.api.getUrl() + 'file');
      http.setRequestHeader('x-api-key', apiKey);
      http.onreadystatechange = () => {
        if (http.readyState === 4) {
          const url = (http.responseText || '').replace(/["]+/g, '');
          if (http.status === 200) {
            resolve(url);
          } else {
            reject(url);
          }
        }
      };
      http.send(formData);
    });
  }

  private getNameOfFile(fileUrl: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const client = new XMLHttpRequest();
      client.open('GET', fileUrl, true);
      client.send();

      client.onreadystatechange = function () {
        if (this.readyState === this.HEADERS_RECEIVED) {
          const contentType = client.getResponseHeader('content-disposition');
          resolve(contentType ? contentType.split('attachment; filename=')[1] : 'Datei');
          client.abort();
        }
      };
    });
  }

  public getFileName(FileUrl: string) {
    if (!this.isUploading && !this.imageNameCache[FileUrl]) {
      this.isUploading = true;
      this.getNameOfFile(FileUrl).then((name) => {
        this.imageNameCache[FileUrl] = name;
        this.isUploading = false;
      });
    }
    return this.imageNameCache[FileUrl] || 'Datei';
  }
}
